import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Plus, Trash2, Pencil, X, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";
import { CardSkeleton } from "./Skeletons";

const TEMPLATES = {
  espresso: ["dose", "ratio", "yield", "temperature", "shot_time", "milk_temp", "garnish"],
  drip:     ["dose", "water", "ratio", "grind", "brew_time", "temperature", "method"],
  freeform: ["ingredient_1", "ingredient_2", "ingredient_3", "build_method", "garnish"],
};

const FIELD_LABEL = {
  dose:          { en: "Dose",         ar: "الجرعة" },
  ratio:         { en: "Ratio",        ar: "النسبة" },
  yield:         { en: "Yield",        ar: "الناتج" },
  temperature:   { en: "Temperature",  ar: "الحرارة" },
  shot_time:     { en: "Shot time",    ar: "زمن السحبة" },
  milk_temp:     { en: "Milk temp",    ar: "حرارة الحليب" },
  garnish:       { en: "Garnish",      ar: "التزيين" },
  water:         { en: "Water",        ar: "الماء" },
  grind:         { en: "Grind",        ar: "الطحن" },
  brew_time:     { en: "Brew time",    ar: "زمن التحضير" },
  method:        { en: "Method",       ar: "الطريقة" },
  ingredient_1:  { en: "Ingredient 1", ar: "المكوّن 1" },
  ingredient_2:  { en: "Ingredient 2", ar: "المكوّن 2" },
  ingredient_3:  { en: "Ingredient 3", ar: "المكوّن 3" },
  build_method:  { en: "Build method", ar: "طريقة التحضير" },
};

export default function CustomTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const { data: drinks = [], isLoading } = useQuery({
    queryKey: ["mycafe-custom"], queryFn: endpoints.myCafeCustom,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return drinks;
    return drinks.filter((d) =>
      [d.name, d.name_ar, d.notes].filter(Boolean).some((s) => s.toLowerCase().includes(q))
    );
  }, [drinks, query]);

  const startNew = () => setEditing({ id: null, name: "", name_ar: "", template: "freeform", fields: {}, notes: "" });
  const startEdit = (d) => setEditing({ ...d, fields: d.fields || {} });
  const cancel = () => setEditing(null);

  const save = useMutation({
    mutationFn: async () => {
      if (!editing.name?.trim()) throw new Error(isRTL ? "اسم المشروب مطلوب" : "Drink name required");
      const payload = { name: editing.name, name_ar: editing.name_ar, template: editing.template, fields: editing.fields, notes: editing.notes };
      return editing.id ? endpoints.myCafeUpdateCustom(editing.id, payload) : endpoints.myCafeCreateCustom(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-custom"] }); toast.success(isRTL ? "تم الحفظ" : "Saved"); cancel(); },
    onError: (e) => toast.error(e.message || "Error"),
  });

  const del = useMutation({
    mutationFn: (id) => endpoints.myCafeDeleteCustom(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-custom"] }); toast.success(isRTL ? "تم الحذف" : "Deleted"); },
  });

  const duplicate = useMutation({
    mutationFn: (d) => endpoints.myCafeCreateCustom({
      name: `${d.name} (${isRTL ? "نسخة" : "copy"})`,
      name_ar: d.name_ar ? `${d.name_ar} (نسخة)` : undefined,
      template: d.template, fields: d.fields, notes: d.notes,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-custom"] }); toast.success(isRTL ? "تم النسخ" : "Duplicated"); },
  });

  const handleDelete = async (d) => {
    const ok = await confirm({
      title: isRTL ? `حذف "${d.name}"؟` : `Delete "${d.name}"?`,
      destructive: true,
      confirmLabel: isRTL ? "نعم، احذف" : "Yes, delete",
    });
    if (ok) del.mutate(d.id);
  };

  const fields = editing ? TEMPLATES[editing.template] : [];

  return (
    <div className="space-y-6" data-testid="mycafe-custom">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-[#5C544F]">
          {isRTL ? "أنشئ مشروبات مبتكرة لا تنتمي للقوالب الأساسية. اختر قالباً، وعبّئ الحقول كما يناسبك." : "Build signature drinks outside the standard espresso/drip templates."}
        </p>
        {!editing && (
          <Button onClick={startNew} className="bg-[#2A2421] text-[#F9F6F0]" data-testid="custom-add-btn">
            <Plus className="w-4 h-4 me-1.5" />
            {isRTL ? "مشروب جديد" : "New drink"}
          </Button>
        )}
      </div>

      {editing && (
        <div className="soft-card rounded-2xl p-6 space-y-5" data-testid="custom-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>{isRTL ? "اسم المشروب" : "Drink name"} *</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5 h-11" data-testid="custom-name-input" />
            </div>
            <div>
              <Label>{isRTL ? "الاسم بالعربي" : "Name (AR)"}</Label>
              <Input value={editing.name_ar || ""} onChange={(e) => setEditing({ ...editing, name_ar: e.target.value })} className="mt-1.5 h-11" data-testid="custom-name-ar-input" />
            </div>
            <div>
              <Label>{isRTL ? "القالب" : "Template"}</Label>
              <Select value={editing.template} onValueChange={(v) => setEditing({ ...editing, template: v, fields: {} })}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="custom-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="freeform">{isRTL ? "حر" : "Freeform"}</SelectItem>
                  <SelectItem value="espresso">{isRTL ? "إسبريسو" : "Espresso"}</SelectItem>
                  <SelectItem value="drip">{isRTL ? "Drip" : "Drip"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {fields.map((f) => (
              <div key={f}>
                <Label className="text-xs">{FIELD_LABEL[f]?.[isRTL ? "ar" : "en"] || f}</Label>
                <Input
                  value={editing.fields?.[f] || ""}
                  onChange={(e) => setEditing({ ...editing, fields: { ...editing.fields, [f]: e.target.value } })}
                  className="mt-1 h-10"
                  data-testid={`custom-field-${f}`}
                />
              </div>
            ))}
          </div>

          <div>
            <Label>{isRTL ? "ملاحظات / خطوات إضافية" : "Notes / extra steps"}</Label>
            <Textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="mt-1.5" data-testid="custom-notes" />
          </div>

          <div className="flex gap-2">
            <Button onClick={() => save.mutate()} className="bg-[#C86240] text-white" data-testid="custom-save-btn">{isRTL ? "حفظ" : "Save"}</Button>
            <Button onClick={cancel} variant="outline"><X className="w-4 h-4 me-1.5" />{isRTL ? "إلغاء" : "Cancel"}</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <CardSkeleton rows={3} />
      ) : drinks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#2A2421]/15 p-12 text-center bg-[#F9F6F0]/50">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#C86240]/10 flex items-center justify-center mb-4">
            <Sparkles className="w-7 h-7 text-[#C86240]" />
          </div>
          <div className="text-[#2A2421] font-medium text-lg">{isRTL ? "لا توجد مشروبات مخصصة بعد" : "No custom drinks yet"}</div>
        </div>
      ) : (
        <>
          {drinks.length > 3 && (
            <div className="relative max-w-sm">
              <Search className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C544F]`} />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={isRTL ? "ابحث..." : "Search..."}
                className={`${isRTL ? "pr-9" : "pl-9"} h-10`}
                data-testid="custom-search"
              />
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((d) => (
              <div key={d.id} className="soft-card rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`custom-card-${d.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className={`text-lg ${isRTL ? "font-serif-ar" : "font-serif-en"} text-[#2A2421] truncate`}>{isRTL && d.name_ar ? d.name_ar : d.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#C86240] mt-0.5">{d.template}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => startEdit(d)} className="p-1.5 hover:bg-[#2A2421]/5 rounded-full" data-testid={`custom-edit-${d.id}`} title={isRTL ? "تعديل" : "Edit"}>
                      <Pencil className="w-3.5 h-3.5 text-[#5C544F]" />
                    </button>
                    <button onClick={() => duplicate.mutate(d)} className="p-1.5 hover:bg-[#2A2421]/5 rounded-full" data-testid={`custom-dup-${d.id}`} title={isRTL ? "تكرار" : "Duplicate"}>
                      <Copy className="w-3.5 h-3.5 text-[#5C544F]" />
                    </button>
                    <button onClick={() => handleDelete(d)} className="p-1.5 hover:bg-red-50 rounded-full" data-testid={`custom-del-${d.id}`} title={isRTL ? "حذف" : "Delete"}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 text-xs text-[#5C544F] grid grid-cols-2 gap-x-2 gap-y-0.5">
                  {Object.entries(d.fields || {}).map(([k, v]) => v ? (
                    <span key={k}><b>{FIELD_LABEL[k]?.[isRTL ? "ar" : "en"] || k}:</b> {v}</span>
                  ) : null)}
                </div>
                {d.notes && <p className="mt-2 text-xs text-[#5C544F] italic">{d.notes}</p>}
              </div>
            ))}
          </div>
        </>
      )}

      {dialog}
    </div>
  );
}
